import { doc, getDoc, getFirestore } from 'firebase/firestore';

// 求人票のPDF / プレビュー画像の取得先URL。
//
// 求人票は卒業年度ごとに作られ、Storage の
//   jobPostingsPdf/{companyId}_{year}_{age}.pdf
//   jobPostingsImages/{companyId}_{year}_{age}.png
// に保存される(functions/jobPostings.js の generateJobPostingPdf / generateJobPostingPdfV2)。
// 以前は年度別エディタ(generateJobPostingPdfV2)の生成物だけ jobPostingsPdf/v2/ ・
// jobPostingsImages/v2/ 配下に保存していたため、その時期に確定した会社のために
// 各年度で v2/ 配下も予備の取得先として残している。
//
// recruitId は searchIndex / recruits のドキュメントID("{companyId}_2027" 形式。
// "_2027" は年度ではなく固定のサフィックス)。

const BUCKET =
  'https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o';

// 学校への送付先を持っている卒業年度(searchIndex の sending_{year})。
export const JOB_POSTING_YEARS = ['2027', '2028'];

const companyIdOf = (recruitId: string): string => recruitId.replace(/_2027$/, '');

// 年度の優先順に、[直下, v2/] の順で候補URLを並べる。
const assetUrls = (
  folder: 'jobPostingsPdf' | 'jobPostingsImages',
  ext: 'pdf' | 'png',
  recruitId: string,
  years: string[],
  age: number | string,
): string[] => {
  const companyId = companyIdOf(recruitId);
  return years.flatMap((year) => [
    `${BUCKET}/${folder}%2F${companyId}_${year}_${age}.${ext}?alt=media`,
    `${BUCKET}/${folder}%2Fv2%2F${companyId}_${year}_${age}.${ext}?alt=media`,
  ]);
};

export const jobPostingPdfUrls = (
  recruitId: string,
  years: string[] = JOB_POSTING_YEARS,
  age: number | string = 20,
): string[] => assetUrls('jobPostingsPdf', 'pdf', recruitId, years, age);

export const jobPostingImageUrls = (
  recruitId: string,
  years: string[] = JOB_POSTING_YEARS,
  age: number | string = 20,
): string[] => assetUrls('jobPostingsImages', 'png', recruitId, years, age);

// 学校に見せる求人票の年度を、優先順に返す。
// その学校が送付先に入っている年度(searchIndex の sending_{year})を新しい順に先頭へ並べ、
// 残りの年度は予備として後ろに付ける(送付先に入っていない学校・デモ校・searchIndex が
// 無い場合は JOB_POSTING_YEARS の順)。searchIndex を取得済みなら data で渡せる。
export const jobPostingYearsForSchool = (
  data: any,
  schoolId?: string,
): string[] => {
  const sentYears = schoolId
    ? JOB_POSTING_YEARS
        .filter((year) => Array.isArray(data?.[`sending_${year}`]) && data[`sending_${year}`].includes(schoolId))
        .reverse()
    : [];
  return [...sentYears, ...JOB_POSTING_YEARS.filter((year) => !sentYears.includes(year))];
};

export async function fetchJobPostingYearsForSchool(
  recruitId: string,
  schoolId?: string,
): Promise<string[]> {
  if (!schoolId) return JOB_POSTING_YEARS;
  try {
    const snap = await getDoc(doc(getFirestore(), 'searchIndex', recruitId));
    return jobPostingYearsForSchool(snap.data(), schoolId);
  } catch {
    return JOB_POSTING_YEARS;
  }
}

const looksLikePdf = (res: Response): boolean =>
  res.ok && (res.headers.get('content-type')?.includes('pdf') ?? false);

// 年度の優先順に求人票PDFを取得する。最初に見つかったPDFを返す。
// どれも取れなかった場合は最後の Response をそのまま返す(呼び出し側で res.ok を判定)。
export async function fetchJobPostingPdf(
  recruitId: string,
  years: string[] = JOB_POSTING_YEARS,
  age: number | string = 20,
): Promise<Response> {
  const urls = jobPostingPdfUrls(recruitId, years, age);
  let last: Response | null = null;
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (looksLikePdf(res)) return res;
      last = res;
    } catch {
      // 取得失敗 → 次の候補へ
    }
  }
  return last ?? new Response(null, { status: 404 });
}
